import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Loader2, Plus, Pencil, Trash2, X, Send, FileText, DollarSign, Upload, Eye, Mail, Paperclip, Receipt, Smartphone, Package, Check } from 'lucide-react';
import { API_URL, downloadPdfWithAuth } from './shared';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const PHONE_KEYWORDS = ['seatouch', 'ocean kit', 'wide-angle conversion', 'customized phone adapter', 'underwater housing'];
const requiresPhoneModel = (name) => {
  const n = (name || '').toLowerCase();
  return PHONE_KEYWORDS.some(kw => n.includes(kw));
};

const InvoicesTab = ({ token, searchQuery }) => {
  const [invoices, setInvoices] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState({ products: [], trips: [], courses: [], events: [], memberships: [] });
  const [orders, setOrders] = useState([]);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  // Doc type of the modal being opened — 'invoice' (default) or 'quote'.
  // Drives label copy, extra fields (valid_until, deposit), and API payload.
  const [docType, setDocType] = useState('invoice');
  // Tab filter for the list view: 'all' | 'invoice' | 'quote'.
  const [listFilter, setListFilter] = useState('all');
  const [invoiceForm, setInvoiceForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '', reference: '',
    items: [], discount_percentage: 0, discount_amount: 0, notes: '', due_date: '', order_id: '',
    doc_type: 'invoice', valid_until: '', deposit_amount: 0, deposit_notes: ''
  });
  const [acceptingQuote, setAcceptingQuote] = useState(null);
  const [sendingQuote, setSendingQuote] = useState(null);
  const [newInvoiceItem, setNewInvoiceItem] = useState({
    name: '', description: '', quantity: 1, unit_price: 0, item_type: 'custom', item_id: null
  });
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [popModalOpen, setPopModalOpen] = useState(false);
  const [selectedInvoiceForPop, setSelectedInvoiceForPop] = useState(null);
  const [popFile, setPopFile] = useState(null);
  const [uploadingPop, setUploadingPop] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(null);
  const [sendingReceipt, setSendingReceipt] = useState(null);
  const [sendingPackingOrder, setSendingPackingOrder] = useState(null);
  const [phoneModels, setPhoneModels] = useState(null);
  const [phoneBrands, setPhoneBrands] = useState({});

  useEffect(() => {
    fetchInvoiceData();
    axios.get(`${API_URL}/phone-models`).then(res => setPhoneModels(res.data)).catch(() => {});
  }, []);

  const fetchInvoiceData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [invoicesRes, itemsRes, ordersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/invoices`, { headers }),
        axios.get(`${API_URL}/admin/invoice-items`, { headers }),
        axios.get(`${API_URL}/admin/orders`, { headers })
      ]);
      setInvoices(invoicesRes.data);
      setInvoiceItems(itemsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      toast.error('Failed to load invoice data');
    }
  };

  const calculateInvoiceTotals = (items, discountPct, discountAmt) => {
    const subtotal = items.reduce((sum, item) => {
      const lineTotal = item.quantity * item.unit_price;
      const linePct = item.line_discount_percent || 0;
      const lineDisc = lineTotal * linePct / 100;
      return sum + (lineTotal - lineDisc);
    }, 0);
    const discount = discountAmt > 0 ? discountAmt : (subtotal * discountPct / 100);
    const total = subtotal - discount;
    return { subtotal, discount, total };
  };

  const handleAddInvoiceItem = () => {
    if (!newInvoiceItem.name || newInvoiceItem.unit_price <= 0) {
      toast.error('Please enter item name and price');
      return;
    }
    setInvoiceForm(prev => ({ ...prev, items: [...prev.items, { ...newInvoiceItem, line_discount_percent: 0 }] }));
    setNewInvoiceItem({ name: '', description: '', quantity: 1, unit_price: 0, item_type: 'custom', item_id: null });
  };

  const handleSelectSiteItem = (item) => {
    setNewInvoiceItem({
      name: item.name,
      description: `${item.type.charAt(0).toUpperCase() + item.type.slice(1)}${item.category ? ` - ${item.category}` : ''}`,
      quantity: 1, unit_price: item.price || 0, item_type: item.type, item_id: item.id
    });
  };

  const handleRemoveInvoiceItem = (index) => {
    setInvoiceForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const handleSelectOrder = (order) => {
    setInvoiceForm(prev => ({
      ...prev,
      customer_name: order.customer_info?.name || '',
      customer_email: order.customer_info?.email || '',
      customer_phone: order.customer_info?.phone || '',
      reference: order.id,
      order_id: order.id,
      items: (order.items || []).map(item => ({
        name: item.name, description: item.description || '',
        quantity: item.quantity || 1, unit_price: item.price || 0,
        item_type: 'order_item', item_id: item.id
      }))
    }));
  };

  const handleCreateInvoice = async () => {
    if (!invoiceForm.customer_name || !invoiceForm.customer_email || invoiceForm.items.length === 0) {
      toast.error('Please fill customer details and add at least one item');
      return;
    }
    if (!invoiceForm.reference) {
      toast.error('Please enter a payment reference');
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Always tag the payload with the current doc type so the backend
      // picks the right numbering scheme (INV- vs QUO-).
      const payload = { ...invoiceForm, doc_type: docType };
      if (editingInvoice) {
        await axios.patch(`${API_URL}/admin/invoices/${editingInvoice.id}`, payload, { headers });
        toast.success(`${docType === 'quote' ? 'Quote' : 'Invoice'} updated`);
      } else {
        await axios.post(`${API_URL}/admin/invoices`, payload, { headers });
        toast.success(`${docType === 'quote' ? 'Quote' : 'Invoice'} created`);
      }
      setInvoiceModalOpen(false);
      setEditingInvoice(null);
      setInvoiceForm({ customer_name: '', customer_email: '', customer_phone: '', reference: '', items: [], discount_percentage: 0, discount_amount: 0, notes: '', due_date: '', order_id: '', doc_type: 'invoice', valid_until: '', deposit_amount: 0, deposit_notes: '' });
      fetchInvoiceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  // Accept a quote → backend converts it in place to an invoice (new INV-
  // number, keeps line items). Also usable directly from the list view.
  const handleAcceptQuote = async (invoiceId) => {
    if (!window.confirm('Accept this quote? It will be converted into an invoice with a new INV- number.')) return;
    setAcceptingQuote(invoiceId);
    try {
      await axios.post(`${API_URL}/admin/invoices/${invoiceId}/accept-quote`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Quote accepted — now an invoice');
      fetchInvoiceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept quote');
    } finally {
      setAcceptingQuote(null);
    }
  };

  // Email a quote to the customer — includes the PDF attachment and a
  // one-click Accept button that hits the public /api/accept-quote/{token}
  // endpoint. Customer can accept from their inbox in a single tap.
  const handleSendQuote = async (invoiceId) => {
    setSendingQuote(invoiceId);
    try {
      const res = await axios.post(`${API_URL}/admin/invoices/${invoiceId}/send-quote`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data?.message || 'Quote sent');
      fetchInvoiceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quote');
    } finally {
      setSendingQuote(null);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    setSendingInvoice(invoiceId);
    try {
      await axios.post(`${API_URL}/admin/invoices/${invoiceId}/send`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invoice sent successfully');
      fetchInvoiceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    } finally {
      setSendingInvoice(null);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await axios.delete(`${API_URL}/admin/invoices/${invoiceId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invoice deleted');
      fetchInvoiceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    setMarkingPaid(invoiceId);
    try {
      await axios.post(`${API_URL}/admin/invoices/${invoiceId}/mark-paid`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invoice marked as paid');
      fetchInvoiceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleUploadPop = async () => {
    if (!popFile || !selectedInvoiceForPop) return;
    setUploadingPop(true);
    try {
      const formData = new FormData();
      formData.append('file', popFile);
      await axios.post(`${API_URL}/admin/invoices/${selectedInvoiceForPop.id}/upload-pop`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Proof of payment uploaded');
      setPopModalOpen(false);
      setPopFile(null);
      setSelectedInvoiceForPop(null);
      fetchInvoiceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload proof of payment');
    } finally {
      setUploadingPop(false);
    }
  };

  const handleSendReceipt = async (invoiceId) => {
    setSendingReceipt(invoiceId);
    try {
      await axios.post(`${API_URL}/admin/invoices/${invoiceId}/send-receipt`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Receipt sent successfully');
      fetchInvoiceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send receipt');
    } finally {
      setSendingReceipt(null);
    }
  };

  const handleSendPackingOrder = async (invoiceId) => {
    setSendingPackingOrder(invoiceId);
    try {
      const res = await axios.post(`${API_URL}/admin/invoices/${invoiceId}/send-packing-order`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchInvoiceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send packing order');
    } finally {
      setSendingPackingOrder(null);
    }
  };

  const openEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setDocType(invoice.doc_type || 'invoice');
    setInvoiceForm({
      customer_name: invoice.customer_name || '', customer_email: invoice.customer_email || '',
      customer_phone: invoice.customer_phone || '', reference: invoice.reference || '',
      items: invoice.items || [], discount_percentage: invoice.discount_percentage || 0,
      discount_amount: invoice.discount_amount || 0, notes: invoice.notes || '',
      due_date: invoice.due_date || '', order_id: invoice.order_id || '',
      doc_type: invoice.doc_type || 'invoice',
      valid_until: invoice.valid_until || '',
      deposit_amount: invoice.deposit_amount || 0,
      deposit_notes: invoice.deposit_notes || '',
    });
    setInvoiceModalOpen(true);
  };

  const filteredInvoices = invoices.filter(inv => {
    // Filter by doc_type tab first — treat missing doc_type as 'invoice' for legacy rows.
    const t = inv.doc_type || 'invoice';
    if (listFilter !== 'all' && t !== listFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(q) ||
      inv.customer_name?.toLowerCase().includes(q) ||
      inv.customer_email?.toLowerCase().includes(q) ||
      inv.reference?.toLowerCase().includes(q)
    );
  });

  const openCreateModal = (type) => {
    setEditingInvoice(null);
    setDocType(type);
    setInvoiceForm({
      customer_name: '', customer_email: '', customer_phone: '', reference: '',
      items: [], discount_percentage: 0, discount_amount: 0, notes: '', due_date: '', order_id: '',
      doc_type: type, valid_until: '', deposit_amount: 0, deposit_notes: '',
    });
    setInvoiceModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <h2 className="text-lg font-semibold">Invoices &amp; Quotes ({filteredInvoices.length})</h2>
          {/* Tab filter — All / Invoices only / Quotes only */}
          <div className="inline-flex rounded-lg border overflow-hidden text-sm" data-testid="invoice-doc-filter">
            {[
              { key: 'all', label: 'All' },
              { key: 'invoice', label: 'Invoices' },
              { key: 'quote', label: 'Quotes' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setListFilter(key)}
                className={`px-3 py-1.5 ${listFilter === key ? 'bg-[#0A2342] text-white' : 'bg-white text-[#0A2342] hover:bg-gray-50'}`}
                data-testid={`invoice-filter-${key}`}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => openCreateModal('quote')}
            variant="outline"
            className="border-[#FF6B35] text-[#FF6B35] hover:bg-[#FFF6F1]"
            data-testid="create-quote-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Quote
          </Button>
          <Button
            onClick={() => openCreateModal('invoice')}
            className="bg-[#FF6B35] hover:bg-[#FF6B35]/90"
            data-testid="create-invoice-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Invoice
          </Button>
        </div>
      </div>

      {/* Invoices List */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0A2342] text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm">Invoice #</th>
                <th className="px-4 py-3 text-left text-sm">Customer</th>
                <th className="px-4 py-3 text-left text-sm">Reference</th>
                <th className="px-4 py-3 text-right text-sm">Total</th>
                <th className="px-4 py-3 text-center text-sm">Status</th>
                <th className="px-4 py-3 text-left text-sm">Created</th>
                <th className="px-4 py-3 text-left text-sm">Sent</th>
                <th className="px-4 py-3 text-center text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No invoices yet. Create your first invoice!
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-[#0A2342]">
                      {invoice.invoice_number}
                      {(invoice.doc_type === 'quote') && (
                        <Badge className="ml-2 bg-[#FF6B35]/10 text-[#FF6B35] border border-[#FF6B35]/30 text-[10px] py-0" data-testid={`quote-badge-${invoice.id}`}>
                          QUOTE
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div>{invoice.customer_name}</div>
                      <div className="text-xs text-gray-500">{invoice.customer_email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">{invoice.reference}</td>
                    <td className="px-4 py-3 text-right font-semibold">R {invoice.total?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={
                        invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                        invoice.status === 'sent' ? 'bg-blue-100 text-blue-700' : 
                        'bg-yellow-100 text-yellow-700'
                      }>
                        {invoice.status === 'paid' ? 'Paid' : invoice.status === 'sent' ? 'Sent' : 'Draft'}
                      </Badge>
                      {invoice.proof_of_payment && (
                        <span className="ml-1 text-xs text-green-600" title="Has proof of payment">
                          <Paperclip className="w-3 h-3 inline" />
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString('en-ZA') : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {invoice.sent_at ? new Date(invoice.sent_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-1 flex-wrap">
                        <Button variant="outline" size="sm" onClick={() => downloadPdfWithAuth(`${API_URL}/admin/invoices/${invoice.id}/pdf`, `invoice-${invoice.invoice_number || invoice.id}.pdf`, token)} title="Download PDF">
                          <FileText className="h-4 w-4" />
                        </Button>
                        {invoice.status !== 'paid' && invoice.doc_type !== 'quote' && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => handleSendInvoice(invoice.id)} disabled={sendingInvoice === invoice.id} title="Send Invoice" className="text-[#FF6B35] border-[#FF6B35] hover:bg-[#FF6B35] hover:text-white">
                              {sendingInvoice === invoice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleMarkPaid(invoice.id)} disabled={markingPaid === invoice.id} title="Mark as Paid" className="text-green-600 border-green-600 hover:bg-green-600 hover:text-white">
                              {markingPaid === invoice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => { setSelectedInvoiceForPop(invoice); setPopFile(null); setPopModalOpen(true); }} title="Upload Proof of Payment" className="text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-white">
                              <Upload className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {invoice.proof_of_payment && (
                          <Button variant="outline" size="sm" onClick={() => window.open(`${API_URL}/admin/invoices/${invoice.id}/pop`, '_blank')} title="View Proof of Payment" className="text-green-600">
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        {invoice.status === 'paid' && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => downloadPdfWithAuth(`${API_URL}/admin/invoices/${invoice.id}/receipt-pdf`, `receipt-${invoice.invoice_number || invoice.id}.pdf`, token)} title="Download Receipt PDF" className="text-green-600 border-green-600">
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleSendReceipt(invoice.id)} disabled={sendingReceipt === invoice.id} title="Send Receipt" className="text-green-600 border-green-600 hover:bg-green-600 hover:text-white">
                              {sendingReceipt === invoice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleSendPackingOrder(invoice.id)} disabled={sendingPackingOrder === invoice.id} title="Send Packing Order" className="text-[#FF6B35] border-[#FF6B35] hover:bg-[#FF6B35] hover:text-white">
                              {sendingPackingOrder === invoice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
                            </Button>
                          </>
                        )}
                        {invoice.doc_type === 'quote' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendQuote(invoice.id)}
                              disabled={sendingQuote === invoice.id}
                              title="Email quote to customer"
                              className="text-[#FF6B35] border-[#FF6B35] hover:bg-[#FF6B35] hover:text-white"
                              data-testid={`send-quote-${invoice.id}`}
                            >
                              {sendingQuote === invoice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAcceptQuote(invoice.id)}
                              disabled={acceptingQuote === invoice.id}
                              title="Accept Quote — converts to invoice"
                              className="text-green-700 border-green-600 hover:bg-green-600 hover:text-white"
                              data-testid={`accept-quote-${invoice.id}`}
                            >
                              {acceptingQuote === invoice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            </Button>
                          </>
                        )}
                        <Button variant="outline" size="sm" onClick={() => openEditInvoice(invoice)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteInvoice(invoice.id)} className="text-red-600 hover:bg-red-50" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice Create/Edit Modal */}
      {invoiceModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#0A2342] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold" data-testid="invoice-modal-title">
                {editingInvoice
                  ? `Edit ${docType === 'quote' ? 'Quote' : 'Invoice'}`
                  : `Create New ${docType === 'quote' ? 'Quote' : 'Invoice'}`}
              </h3>
              <button onClick={() => setInvoiceModalOpen(false)} className="text-white hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Quick Fill from Order */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-[#0A2342] mb-2">Quick Fill from Existing Order</h4>
                <select className="w-full border rounded-lg px-3 py-2" onChange={(e) => { const order = orders.find(o => o.id === e.target.value); if (order) handleSelectOrder(order); }} value={invoiceForm.order_id || ''}>
                  <option value="">-- Select an order to pre-fill --</option>
                  {orders.map(order => (
                    <option key={order.id} value={order.id}>
                      {order.id.slice(0, 8)}... - {order.customer_info?.name} - R {order.total?.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>

              {/* Customer Details */}
              <div>
                <h4 className="font-medium text-[#0A2342] mb-3">Customer Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Customer Name *</label>
                    <Input value={invoiceForm.customer_name} onChange={(e) => setInvoiceForm(prev => ({ ...prev, customer_name: e.target.value }))} placeholder="Full name" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Email *</label>
                    <Input type="email" value={invoiceForm.customer_email} onChange={(e) => setInvoiceForm(prev => ({ ...prev, customer_email: e.target.value }))} placeholder="email@example.com" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Phone</label>
                    <Input value={invoiceForm.customer_phone} onChange={(e) => setInvoiceForm(prev => ({ ...prev, customer_phone: e.target.value }))} placeholder="+27..." />
                  </div>
                </div>
              </div>

              {/* Reference & Due Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Payment Reference *</label>
                  <Input value={invoiceForm.reference} onChange={(e) => setInvoiceForm(prev => ({ ...prev, reference: e.target.value }))} placeholder="e.g., Customer name or Order ID" />
                  <p className="text-xs text-gray-500 mt-1">Customer will use this as bank reference</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    {docType === 'quote' ? 'Valid Until' : 'Due Date'}
                  </label>
                  {docType === 'quote' ? (
                    <Input
                      type="date"
                      value={invoiceForm.valid_until}
                      onChange={(e) => setInvoiceForm(prev => ({ ...prev, valid_until: e.target.value }))}
                      data-testid="invoice-valid-until"
                    />
                  ) : (
                    <Input
                      type="date"
                      value={invoiceForm.due_date}
                      onChange={(e) => setInvoiceForm(prev => ({ ...prev, due_date: e.target.value }))}
                    />
                  )}
                  {docType === 'quote' && (
                    <p className="text-xs text-gray-500 mt-1">Defaults to 30 days from today if left blank</p>
                  )}
                </div>
              </div>

              {/* Non-refundable deposit — shown on both quotes and invoices,
                  but really targeted at quotes so customers know they need
                  to pay a deposit up-front to secure the booking. */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="deposit-block">
                <h4 className="font-medium text-[#0A2342] mb-2">Non-refundable Deposit <span className="text-xs font-normal text-gray-500">(optional)</span></h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Deposit Amount (R)</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={invoiceForm.deposit_amount || ''}
                      onChange={(e) => setInvoiceForm(prev => ({ ...prev, deposit_amount: Number(e.target.value) || 0 }))}
                      placeholder="0.00"
                      data-testid="deposit-amount-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Deposit Notes</label>
                    <Input
                      value={invoiceForm.deposit_notes || ''}
                      onChange={(e) => setInvoiceForm(prev => ({ ...prev, deposit_notes: e.target.value }))}
                      placeholder="e.g. Payable within 7 days to secure the booking"
                      data-testid="deposit-notes-input"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 mt-2">
                  If set, a highlighted &quot;NON-REFUNDABLE DEPOSIT&quot; block will appear on the PDF above the banking details.
                </p>
              </div>

              {/* Add Items */}
              <div>
                <h4 className="font-medium text-[#0A2342] mb-3">Invoice Items</h4>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-gray-600 mb-2">Add from site items:</p>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
                    <select className="border rounded px-2 py-1 text-sm" onChange={(e) => { const item = invoiceItems.products.find(p => p.id === e.target.value); if (item) handleSelectSiteItem(item); }} value="">
                      <option value="">Products...</option>
                      {invoiceItems.products.map(p => (<option key={p.id} value={p.id}>{p.name} - R{p.price}</option>))}
                    </select>
                    <select className="border rounded px-2 py-1 text-sm" onChange={(e) => { const item = (invoiceItems.brand_products || []).find(bp => bp.id === e.target.value); if (item) handleSelectSiteItem(item); }} value="">
                      <option value="">DiveVolk / Brands...</option>
                      {(invoiceItems.brand_products || []).map(bp => (<option key={bp.id} value={bp.id}>{bp.name}{bp.price_on_request ? ' - POA' : ` - R${bp.price}`}</option>))}
                    </select>
                    <select className="border rounded px-2 py-1 text-sm" onChange={(e) => { const item = invoiceItems.trips.find(t => t.id === e.target.value); if (item) handleSelectSiteItem(item); }} value="">
                      <option value="">Trips...</option>
                      {invoiceItems.trips.map(t => (<option key={t.id} value={t.id}>{t.name} - R{t.price}</option>))}
                    </select>
                    <select className="border rounded px-2 py-1 text-sm" onChange={(e) => { const item = invoiceItems.courses.find(c => c.id === e.target.value); if (item) handleSelectSiteItem(item); }} value="">
                      <option value="">Courses...</option>
                      {invoiceItems.courses.map(c => (<option key={c.id} value={c.id}>{c.name} - R{c.price}</option>))}
                    </select>
                    <select className="border rounded px-2 py-1 text-sm" onChange={(e) => { const item = invoiceItems.events.find(ev => ev.id === e.target.value); if (item) handleSelectSiteItem(item); }} value="">
                      <option value="">Events...</option>
                      {invoiceItems.events.map(ev => (<option key={ev.id} value={ev.id}>{ev.name} - R{ev.price}</option>))}
                    </select>
                    <select className="border rounded px-2 py-1 text-sm" onChange={(e) => { const item = invoiceItems.memberships?.find(m => m.id === e.target.value); if (item) handleSelectSiteItem(item); }} value="">
                      <option value="">Memberships...</option>
                      {(invoiceItems.memberships || []).map(m => (<option key={m.id} value={m.id}>{m.name} - R{m.price}</option>))}
                    </select>
                  </div>

                  <p className="text-sm text-gray-600 mb-2">Or add custom item:</p>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-4">
                      <Input placeholder="Item name" value={newInvoiceItem.name} onChange={(e) => setNewInvoiceItem(prev => ({ ...prev, name: e.target.value }))} />
                    </div>
                    <div className="col-span-3">
                      <Input placeholder="Description" value={newInvoiceItem.description} onChange={(e) => setNewInvoiceItem(prev => ({ ...prev, description: e.target.value }))} />
                    </div>
                    <div className="col-span-1">
                      <Input type="number" min="1" placeholder="Qty" value={newInvoiceItem.quantity} onChange={(e) => setNewInvoiceItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))} />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" min="0" step="0.01" placeholder="Price" value={newInvoiceItem.unit_price || ''} onChange={(e) => setNewInvoiceItem(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))} />
                    </div>
                    <div className="col-span-2">
                      <Button onClick={handleAddInvoiceItem} className="w-full bg-[#0A2342]">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {invoiceForm.items.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left text-sm">Item</th>
                          <th className="px-3 py-2 text-center text-sm">Qty</th>
                          <th className="px-3 py-2 text-right text-sm">Unit Price</th>
                          <th className="px-3 py-2 text-right text-sm">Discount</th>
                          <th className="px-3 py-2 text-right text-sm">Total</th>
                          <th className="px-3 py-2 text-center text-sm"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceForm.items.map((item, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2">
                              <div className="font-medium">{item.name}</div>
                              {item.description && <div className="text-xs text-gray-500">{item.description}</div>}
                              {requiresPhoneModel(item.name) && phoneModels && (
                                <div className="mt-2 flex flex-col gap-1">
                                  <div className="flex items-center gap-1 text-xs text-blue-600 font-medium">
                                    <Smartphone className="w-3 h-3" /> Phone Model:
                                  </div>
                                  <div className="flex gap-1">
                                    <Select
                                      value={phoneBrands[idx] || ''}
                                      onValueChange={(v) => {
                                        setPhoneBrands(prev => ({ ...prev, [idx]: v }));
                                        const updated = [...invoiceForm.items];
                                        updated[idx] = { ...updated[idx], phone_model: null };
                                        setInvoiceForm(prev => ({ ...prev, items: updated }));
                                      }}
                                    >
                                      <SelectTrigger className="h-8 text-xs w-32"><SelectValue placeholder="Brand" /></SelectTrigger>
                                      <SelectContent>
                                        {phoneModels.brand_list.map(b => (<SelectItem key={b} value={b}>{b}</SelectItem>))}
                                      </SelectContent>
                                    </Select>
                                    <Select
                                      value={item.phone_model || ''}
                                      onValueChange={(v) => {
                                        const updated = [...invoiceForm.items];
                                        updated[idx] = { ...updated[idx], phone_model: v };
                                        setInvoiceForm(prev => ({ ...prev, items: updated }));
                                      }}
                                      disabled={!phoneBrands[idx]}
                                    >
                                      <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder={phoneBrands[idx] ? "Model" : "Brand first"} /></SelectTrigger>
                                      <SelectContent>
                                        {(phoneModels.brands[phoneBrands[idx]] || []).map(m => (<SelectItem key={m} value={`${phoneBrands[idx]} ${m}`}>{m}</SelectItem>))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  {item.phone_model && <div className="text-xs text-green-600">{item.phone_model}</div>}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">{item.quantity}</td>
                            <td className="px-3 py-2 text-right">R {item.unit_price?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Input type="number" min="0" max="100" step="1" className="w-20 h-8 text-right text-sm" placeholder="0" value={item.line_discount_percent || ''} onChange={(e) => {
                                  const updated = [...invoiceForm.items];
                                  updated[idx] = { ...updated[idx], line_discount_percent: parseFloat(e.target.value) || 0 };
                                  setInvoiceForm(prev => ({ ...prev, items: updated }));
                                }} />
                                <span className="text-xs text-gray-500">%</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right font-medium">R {((item.quantity * item.unit_price) * (1 - (item.line_discount_percent || 0) / 100))?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-center">
                              <button onClick={() => handleRemoveInvoiceItem(idx)} className="text-red-500 hover:text-red-700">
                                <X className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Discount */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Discount %</label>
                  <Input type="number" min="0" max="100" value={invoiceForm.discount_percentage || ''} onChange={(e) => setInvoiceForm(prev => ({ ...prev, discount_percentage: parseFloat(e.target.value) || 0, discount_amount: 0 }))} placeholder="e.g., 10" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Or Fixed Discount (R)</label>
                  <Input type="number" min="0" value={invoiceForm.discount_amount || ''} onChange={(e) => setInvoiceForm(prev => ({ ...prev, discount_amount: parseFloat(e.target.value) || 0, discount_percentage: 0 }))} placeholder="e.g., 500" />
                </div>
              </div>

              {/* Totals */}
              {invoiceForm.items.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  {(() => {
                    const { subtotal, discount, total } = calculateInvoiceTotals(invoiceForm.items, invoiceForm.discount_percentage, invoiceForm.discount_amount);
                    return (
                      <div className="space-y-2">
                        <div className="flex justify-between"><span>Subtotal:</span><span className="font-medium">R {subtotal.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span></div>
                        {discount > 0 && (
                          <div className="flex justify-between text-green-600"><span>Discount{invoiceForm.discount_percentage > 0 ? ` (${invoiceForm.discount_percentage}%)` : ''}:</span><span>-R {discount.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span></div>
                        )}
                        <div className="flex justify-between text-lg font-bold border-t pt-2"><span>Total:</span><span className="text-[#FF6B35]">R {total.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span></div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">Notes (optional)</label>
                <textarea className="w-full border rounded-lg px-3 py-2" rows="3" value={invoiceForm.notes} onChange={(e) => setInvoiceForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Additional notes for the invoice..." />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setInvoiceModalOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateInvoice} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90">
                  {editingInvoice
                    ? `Update ${docType === 'quote' ? 'Quote' : 'Invoice'}`
                    : `Create ${docType === 'quote' ? 'Quote' : 'Invoice'}`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proof of Payment Upload Modal */}
      {popModalOpen && selectedInvoiceForPop && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="bg-[#0A2342] text-white px-6 py-4 flex justify-between items-center rounded-t-lg">
              <h3 className="text-lg font-semibold">Upload Proof of Payment</h3>
              <button onClick={() => setPopModalOpen(false)} className="text-white hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-gray-600">
                <p><strong>Invoice:</strong> {selectedInvoiceForPop.invoice_number}</p>
                <p><strong>Customer:</strong> {selectedInvoiceForPop.customer_name}</p>
                <p><strong>Amount:</strong> R {selectedInvoiceForPop.total?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-2">Select File (Image or PDF)</label>
                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => setPopFile(e.target.files[0])} className="w-full border rounded-lg px-3 py-2" />
                {popFile && (<p className="text-xs text-gray-500 mt-1">Selected: {popFile.name}</p>)}
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setPopModalOpen(false)}>Cancel</Button>
                <Button onClick={handleUploadPop} disabled={!popFile || uploadingPop} className="bg-green-600 hover:bg-green-700">
                  {uploadingPop ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload & Mark Paid
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesTab;
